import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  BackgroundVariant,
  MiniMap,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  NodeChange,
  EdgeChange,
  Connection,
  ReactFlowProvider,
  MarkerType,
  ConnectionMode,
  ConnectionLineType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import './workflow-editor.css';
import { validateWorkflowStructure, formatZodErrors } from '../../utils/validation';

import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { WorkflowDefinition, WorkflowNode, WorkflowEdge } from '../../types/workflow';
import Sidebar from './components/Sidebar';
import Toolbar from './components/Toolbar';
import PropertyModal from './components/PropertyModal';
import ValidationHelper from './components/ValidationHelper';
import TipsPanel from './components/TipsPanel';
import WorkflowFooter from './components/WorkflowFooter';
import StartNode from './components/nodes/StartNode';
import EndNode from './components/nodes/EndNode';
import ActivityNode from './components/nodes/ActivityNode';
import ConditionNode from './components/nodes/ConditionNode';
import ParallelNode from './components/nodes/ParallelNode';

const nodeTypes = {
  START: StartNode,
  END: EndNode,
  ACTIVITY: ActivityNode,
  CONDITION: ConditionNode,
  PARALLEL_SPLIT: ParallelNode,
  PARALLEL_JOIN: ParallelNode,
};

const WorkflowEditorContent: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isNewWorkflow = location.pathname === '/workflows/new';
  const isReadOnly = !isAdmin && !isNewWorkflow;

  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [showValidationHelper, setShowValidationHelper] = useState(false);
  const [currentValidationErrors, setCurrentValidationErrors] = useState<string[]>([]);
  const [workflowName, setWorkflowName] = useState('');
  const [workflowDescription, setWorkflowDescription] = useState('');
  const [workflowCategory, setWorkflowCategory] = useState('APPROVAL');
  const [isDirty, setIsDirty] = useState(false);

  // Fetch workflow data
  const { data: workflow, isLoading } = useQuery({
    queryKey: ['workflow', id],
    queryFn: async () => {
      if (isNewWorkflow) return null;
      const response = await api.get(`/workflows/${id}`);
      return response.data as WorkflowDefinition;
    },
    enabled: !isNewWorkflow,
  });

  // Convert workflow data to React Flow format
  useEffect(() => {
    if (workflow) {
      setWorkflowName(workflow.name);
      setWorkflowDescription(workflow.description || '');
      setWorkflowCategory(workflow.category);

      // Convert nodes
      const flowNodes = workflow.nodes?.map((node: WorkflowNode) => ({
        id: node.nodeId,
        type: node.type,
        position: { x: node.positionX, y: node.positionY },
        data: {
          label: node.name,
          activityType: node.activityType,
          configuration: node.configuration,
          description: node.description,
        },
      })) || [];

      // Convert edges
      const flowEdges = workflow.edges?.map((edge: WorkflowEdge) => {
        const handles = edge.condition?.handles || {};
        return {
          id: edge.edgeId,
          source: edge.sourceNodeId,
          target: edge.targetNodeId,
          sourceHandle: handles.sourceHandle || null,
          targetHandle: handles.targetHandle || null,
          type: 'smoothstep',
          animated: false,
          label: edge.label,
          data: edge.condition,
          style: { stroke: edge.type === 'CONDITIONAL' ? '#f59e0b' : '#6b7280', strokeWidth: 2 },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 20,
            height: 20,
            color: edge.type === 'CONDITIONAL' ? '#f59e0b' : '#6b7280',
          },
        };
      }) || [];

      setNodes(flowNodes);
      setEdges(flowEdges);
    } else if (isNewWorkflow) {
      // Create default nodes for new workflow
      setNodes([
        {
          id: 'start-1',
          type: 'START',
          position: { x: 50, y: 100 },
          data: { label: 'Start' },
        },
        {
          id: 'end-1',
          type: 'END',
          position: { x: 400, y: 100 },
          data: { label: 'End' },
        },
      ]);
    }
  }, [workflow, isNewWorkflow]);

  // Save workflow mutation
  const saveMutation = useMutation({
    mutationFn: async (data: any) => {
      const isNew = location.pathname === '/workflows/new';
      if (isNew) {
        // For new workflows, first create with basic metadata
        const { name, description, category } = data;
        const createResponse = await api.post('/workflows', { name, description, category });
        // Then immediately update with nodes and edges
        if (createResponse.data?.id) {
          return await api.put(`/workflows/${createResponse.data.id}`, data);
        }
        return createResponse;
      } else {
        return await api.put(`/workflows/${id}`, data);
      }
    },
    onSuccess: (response) => {
      // Check for validation warnings
      if (response.data?.validationWarnings) {
        toast.success('Workflow saved with warnings');
        response.data.validationWarnings.forEach((warning: string) => {
          toast(warning, { 
            icon: '⚠️',
            duration: 5000,
            style: {
              background: '#FEF3C7',
              color: '#92400E',
            },
          });
        });
      } else {
        toast.success('Workflow saved successfully');
      }
      
      setIsDirty(false);
      if (isNewWorkflow && response.data?.id) {
        navigate(`/workflows/${response.data.id}`);
      }
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      queryClient.invalidateQueries({ queryKey: ['workflow', id] });
    },
    onError: (error: any) => {
      console.error('Save error:', error.response?.data);
      
      const errorData = error.response?.data;
      if (errorData?.details) {
        // Handle Zod validation errors
        if (Array.isArray(errorData.details)) {
          toast.error('Validation failed:');
          const formattedErrors = formatZodErrors(errorData.details);
          formattedErrors.forEach((message: string) => {
            toast.error(`• ${message}`, { duration: 5000 });
          });
        } else if (typeof errorData.details === 'string') {
          toast.error(errorData.details);
        } else {
          toast.error(errorData.message || 'Failed to save workflow');
        }
      } else {
        toast.error(errorData?.message || 'Failed to save workflow');
      }
    },
  });

  // Publish workflow mutation
  const publishMutation = useMutation({
    mutationFn: async () => {
      return await api.post(`/workflows/${id}/publish`);
    },
    onSuccess: () => {
      toast.success('Workflow published successfully');
      queryClient.invalidateQueries({ queryKey: ['workflow', id] });
    },
    onError: (error: any) => {
      const errors = error.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        errors.forEach((err: string) => toast.error(err));
      } else {
        toast.error(error.response?.data?.message || 'Failed to publish workflow');
      }
    },
  });

  // Validate workflow mutation
  const validateMutation = useMutation({
    mutationFn: async () => {
      return await api.post(`/workflows/${id}/validate`);
    },
    onSuccess: (response) => {
      if (response.data?.isValid) {
        toast.success('✓ Workflow validation passed!', {
          icon: '✅',
          duration: 3000,
        });
      } else {
        toast.error('Workflow validation failed:', { duration: 3000 });
        response.data?.errors?.forEach((err: string) => {
          toast.error(`• ${err}`, { 
            duration: 6000,
            style: {
              paddingLeft: '20px',
            },
          });
        });
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to validate workflow');
    },
  });

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      if (isReadOnly) return;
      setNodes((nds) => applyNodeChanges(changes, nds));
      setIsDirty(true);
    },
    [isReadOnly]
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      if (isReadOnly) return;
      setEdges((eds) => applyEdgeChanges(changes, eds));
      setIsDirty(true);
    },
    [isReadOnly]
  );

  const onConnect = useCallback(
    (params: Connection) => {
      if (isReadOnly) return;
      
      const newEdge = {
        ...params,
        id: `edge-${Date.now()}`,
        type: 'smoothstep',
        animated: false,
        style: { stroke: '#6b7280', strokeWidth: 2 },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 20,
          height: 20,
          color: '#6b7280',
        },
      };
      
      setEdges((eds) => addEdge(newEdge, eds));
      setIsDirty(true);
    },
    [isReadOnly]
  );

  const onNodeClick = useCallback((_event: React.MouseEvent, node: Node) => {
    setSelectedNode(node);
    // Single click just selects the node, double-click opens modal
  }, []);

  const onNodeDoubleClick = useCallback((_event: React.MouseEvent, node: Node) => {
    setSelectedNode(node);
    setShowPropertyModal(true);
  }, []);

  const onEdgeClick = useCallback((_event: React.MouseEvent, _edge: Edge) => {
    if (isReadOnly) return;
    setSelectedNode(null);
  }, [isReadOnly]);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
    setShowPropertyModal(false);
  }, []);

  const onEdgeDoubleClick = useCallback(
    (event: React.MouseEvent, edge: Edge) => {
      if (isReadOnly) return;
      event.stopPropagation();
      
      const label = prompt('Enter edge label (e.g., "Yes", "No", "Approved"):', String(edge.label || ''));
      if (label !== null) {
        setEdges((eds) =>
          eds.map((e) =>
            e.id === edge.id
              ? { ...e, label, animated: false }
              : e
          )
        );
        setIsDirty(true);
      }
    },
    [isReadOnly]
  );

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      if (isReadOnly) return;
      
      event.preventDefault();
      const type = event.dataTransfer.getData('nodeType');
      const activityType = event.dataTransfer.getData('activityType');
      
      if (!type) return;

      const reactFlowBounds = event.currentTarget.getBoundingClientRect();
      const position = {
        x: event.clientX - reactFlowBounds.left - 30,
        y: event.clientY - reactFlowBounds.top - 15,
      };

      const newNode: Node = {
        id: `${type.toLowerCase()}-${Date.now()}`,
        type,
        position,
        data: {
          label: activityType || type,
          activityType: activityType || undefined,
        },
      };

      setNodes((nds) => nds.concat(newNode));
      setIsDirty(true);
    },
    [isReadOnly]
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const handleSave = useCallback(() => {
    if (!workflowName) {
      toast.error('Workflow name is required');
      return;
    }

    // Client-side validation
    const validationErrors = validateWorkflowStructure(nodes, edges);
    
    // Show validation errors if any
    if (validationErrors.length > 0) {
      toast.error('🔴 Cannot save - Please fix these issues:', { 
        duration: 4000,
        style: {
          background: '#FEE2E2',
          color: '#991B1B',
          fontWeight: 'bold',
        },
      });
      validationErrors.forEach(error => {
        toast(error, { 
          duration: 6000,
          icon: '',
          style: { 
            paddingLeft: '16px',
            background: '#FFF',
            border: '1px solid #FCA5A5',
            color: '#7F1D1D',
          },
        });
      });
      return;
    }

    const workflowData = {
      name: workflowName,
      description: workflowDescription,
      category: workflowCategory,
      nodes: nodes.map((node) => {
        // Ensure ACTIVITY nodes have a valid activityType
        let activityType = node.data?.activityType;
        if (node.type === 'ACTIVITY' && !activityType) {
          // Default to USER_TASK if not set
          activityType = 'USER_TASK';
          console.warn(`Node ${node.id} is missing activityType, defaulting to USER_TASK`);
        }
        
        return {
          nodeId: node.id,
          type: node.type,
          activityType: node.type === 'ACTIVITY' ? activityType : undefined,
          name: node.data?.label || node.type,
          description: node.data?.description,
          positionX: node.position.x,
          positionY: node.position.y,
          configuration: node.data?.configuration,
          style: node.style,
        };
      }),
      edges: edges.map((edge) => ({
        edgeId: edge.id,
        sourceNodeId: edge.source,
        targetNodeId: edge.target,
        type: edge.label ? 'CONDITIONAL' : 'DEFAULT',
        label: edge.label || '',
        condition: {
          ...edge.data,
          handles: {
            sourceHandle: edge.sourceHandle || null,
            targetHandle: edge.targetHandle || null,
          },
        },
      })),
    };

    saveMutation.mutate(workflowData);
  }, [workflowName, workflowDescription, workflowCategory, nodes, edges, saveMutation]);

  const handlePublish = () => {
    if (!id || isNewWorkflow) return;
    publishMutation.mutate();
  };

  const handleValidate = () => {
    if (!id || isNewWorkflow) {
      // For new workflows, run client-side validation only
      const validationErrors = validateWorkflowStructure(nodes, edges);
      
      setCurrentValidationErrors(validationErrors);
      setShowValidationHelper(true);
      
      if (validationErrors.length === 0) {
        toast.success('✅ Perfect! Your workflow is valid and ready to save.', {
          duration: 3000,
          style: {
            background: '#D1FAE5',
            color: '#065F46',
            fontWeight: '500',
          },
        });
      } else {
        toast('🔍 Validation found issues to fix:', { 
          duration: 4000,
          icon: '',
          style: {
            background: '#FEF3C7',
            color: '#78350F',
            fontWeight: 'bold',
          },
        });
        validationErrors.forEach(error => {
          toast(error, { 
            duration: 7000,
            icon: '',
            style: { 
              paddingLeft: '16px',
              background: '#FFFBEB',
              border: '1px solid #FCD34D',
              color: '#78350F',
            },
          });
        });
      }
      return;
    }
    validateMutation.mutate();
  };

  const updateNodeData = useCallback(
    (nodeId: string, data: any) => {
      if (isReadOnly) return;
      
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === nodeId) {
            return {
              ...node,
              data: {
                ...node.data,
                ...data,
              },
            };
          }
          return node;
        })
      );
      setIsDirty(true);
    },
    [isReadOnly]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg text-gray-600">Loading workflow...</div>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-6rem)] bg-gray-50">
      {/* Sidebar - Activity Palette */}
      {!isReadOnly && <Sidebar />}

      {/* Main Canvas Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Toolbar */}
        <Toolbar
          workflowName={workflowName}
          workflowDescription={workflowDescription}
          workflowCategory={workflowCategory}
          onNameChange={(name) => {
            setWorkflowName(name);
            setIsDirty(true);
          }}
          onDescriptionChange={(desc) => {
            setWorkflowDescription(desc);
            setIsDirty(true);
          }}
          onCategoryChange={(cat) => {
            setWorkflowCategory(cat);
            setIsDirty(true);
          }}
          onSave={handleSave}
          onPublish={handlePublish}
          onValidate={handleValidate}
          isDirty={isDirty}
          isReadOnly={isReadOnly}
          isNewWorkflow={isNewWorkflow}
          isSaving={saveMutation.isPending}
          workflow={workflow}
          hasValidationErrors={currentValidationErrors.length > 0}
        />

        {/* Canvas */}
        <div className="flex-1 relative overflow-auto workflow-canvas-container">
          <div style={{ width: '2500px', height: '1200px' }}>
            <ReactFlow
              nodes={nodes}
              edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onNodeDoubleClick={onNodeDoubleClick}
            onEdgeClick={onEdgeClick}
            onEdgeDoubleClick={onEdgeDoubleClick}
            onPaneClick={onPaneClick}
            onDrop={onDrop}
            onDragOver={onDragOver}
            nodeTypes={nodeTypes}
            fitView={false}
            defaultViewport={{ x: 100, y: 100, zoom: 0.8 }}
            minZoom={0.25}
            maxZoom={2}
            deleteKeyCode={isReadOnly ? null : ['Delete', 'Backspace']}
            multiSelectionKeyCode={isReadOnly ? null : ['Meta', 'Ctrl']}
            selectionOnDrag={!isReadOnly}
            className="react-flow-canvas"
            style={{ width: '100%', height: '100%' }}
            panOnScroll={false}
            panOnDrag={true}
            zoomOnScroll={true}
            zoomOnPinch={true}
            preventScrolling={true}
            connectionMode={ConnectionMode.Loose}
            connectionLineType={ConnectionLineType.SmoothStep}
            connectionLineStyle={{ stroke: '#6b7280', strokeWidth: 2 }}
            defaultEdgeOptions={{
              type: 'smoothstep',
              animated: false,
              style: { stroke: '#6b7280', strokeWidth: 2 },
              markerEnd: {
                type: MarkerType.ArrowClosed,
                width: 20,
                height: 20,
                color: '#6b7280',
              },
            }}
            snapToGrid={true}
            snapGrid={[10, 10]}
          >
            <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
            <Controls />
            <MiniMap
              nodeColor={(node) => {
                switch (node.type) {
                  case 'START':
                    return '#10b981';
                  case 'END':
                    return '#ef4444';
                  case 'CONDITION':
                    return '#f59e0b';
                  case 'PARALLEL_SPLIT':
                  case 'PARALLEL_JOIN':
                    return '#8b5cf6';
                  default:
                    return '#3b82f6';
                }
              }}
              style={{
                backgroundColor: '#f3f4f6',
                height: 100,
                width: 150,
              }}
              nodeStrokeWidth={3}
              zoomable
              pannable
            />
            </ReactFlow>
          </div>
          
          {/* Validation Helper Panel */}
          <ValidationHelper
            isVisible={showValidationHelper}
            validationErrors={currentValidationErrors}
            onClose={() => setShowValidationHelper(false)}
          />
          
          {/* Tips Panel - Inside Canvas Area */}
          <TipsPanel />
        </div>
      </div>

      {/* Property Modal */}
      <PropertyModal
        node={selectedNode}
        isOpen={showPropertyModal}
        onClose={() => {
          setShowPropertyModal(false);
          setSelectedNode(null);
        }}
        onUpdate={updateNodeData}
        isReadOnly={isReadOnly}
      />

      {/* Fixed Footer */}
      <WorkflowFooter />
    </div>
  );
};

const WorkflowEditor: React.FC = () => {
  return (
    <ReactFlowProvider>
      <WorkflowEditorContent />
    </ReactFlowProvider>
  );
};

export default WorkflowEditor;